/**
 * Motion residue normalizer — removes the inline declarations a source site's
 * JavaScript wrote before the crawler serialized the DOM, leaving authored
 * markup untouched.
 *
 * Intentionally DOM-only so it can run against jsdom in Node, native DOMParser
 * output in the browser, and linkedom inside the Cloudflare Worker. It takes a
 * root Element and mutates it in place. Same contract as
 * `editing/logo-normalizer.js`.
 *
 * Style attributes are read and rewritten through css-tree, never through
 * `element.style`: linkedom enumerates custom property names but cannot read or
 * remove them (`getPropertyValue('--scrollPos')` returns undefined,
 * `removeProperty` is a no-op), so a CSSStyleDeclaration would silently fail on
 * the Worker. Declarations that survive are copied byte-for-byte from the
 * original attribute text, so nothing this module leaves alone can drift.
 *
 * Remove/rewrite only, and idempotent: `normalize(normalize(x))` equals
 * `normalize(x)`.
 */

import * as csstree from '../vendor/csstree.esm.js';
import { MOTION_PATTERNS } from './motion-patterns.js';

export const ALL_ACTIONS = ['preserve', 'strip', 'reset', 'removeNodes', 'neutralize', 'stamp'];

const TRANSLATE_FUNCTIONS = new Set([
  'translate',
  'translatex',
  'translatey',
  'translatez',
  'translate3d'
]);

function safeMatches(el, selector) {
  try {
    return !!(el && el.matches && el.matches(selector));
  } catch {
    return false;
  }
}

function safeQueryAll(root, selector) {
  try {
    return Array.from(root.querySelectorAll(selector));
  } catch {
    return [];
  }
}

/**
 * Elements a selector claims, including the root itself — template sections are
 * handed to this module as their own root element, and redrockwebdesign's
 * reading-progress bar IS the section root.
 */
function elementsFor(root, selectors) {
  const found = new Set();
  for (const selector of selectors) {
    if (safeMatches(root, selector)) found.add(root);
    for (const el of safeQueryAll(root, selector)) found.add(el);
  }
  return found;
}

function numbersIn(node) {
  const values = [];
  csstree.walk(node, {
    visit: 'Number',
    enter(child) {
      values.push(child.value);
    }
  });
  return values;
}

/**
 * `matrix(1, 0, 0, 1, tx, ty)` is a pure translation; any other coefficient set
 * carries rotation, scale or skew and is authored.
 */
function isTranslationMatrix(node) {
  const numbers = numbersIn(node);
  if (numbers.length < 4) return false;
  const [a, b, c, d] = numbers;
  return a === '1' && b === '0' && c === '0' && d === '1';
}

/**
 * True when a transform is built only from translation. Guards the one class of
 * authored inline transform in the corpus — oakharborwebdesigns flips 33 images
 * with `scaleY(-1)`, which a blanket transform strip would turn upside down.
 */
function isTranslateOnlyTransform(value) {
  const text = String(value || '').trim();
  if (!text) return false;
  if (text.toLowerCase() === 'none') return true;

  let ast;
  try {
    ast = csstree.parse(text, { context: 'value' });
  } catch {
    return false;
  }

  let functionCount = 0;
  let translateOnly = true;
  csstree.walk(ast, {
    visit: 'Function',
    enter(node) {
      functionCount += 1;
      const name = String(node.name || '').toLowerCase();
      if (name === 'matrix') {
        if (!isTranslationMatrix(node)) translateOnly = false;
        return;
      }
      if (!TRANSLATE_FUNCTIONS.has(name)) translateOnly = false;
    }
  });

  return functionCount > 0 && translateOnly;
}

function isSingleNumeric(value) {
  let ast;
  try {
    ast = csstree.parse(String(value || '').trim(), { context: 'value' });
  } catch {
    return false;
  }
  const kinds = [];
  csstree.walk(ast, (node) => {
    if (node.type === 'Number' || node.type === 'Dimension' || node.type === 'Percentage') {
      kinds.push(node.type);
    }
  });
  return kinds.length === 1;
}

export function valueMatchesKind(value, kind) {
  switch (kind) {
    case 'any':
      return true;
    case 'translate-only':
      return isTranslateOnlyTransform(value);
    case 'numeric':
      return isSingleNumeric(value);
    case 'zero-or-one': {
      const text = String(value || '').trim();
      return text === '0' || text === '1';
    }
    default:
      return false;
  }
}

function parseDeclarations(styleText) {
  const source = String(styleText || '');
  let ast;
  try {
    ast = csstree.parse(source, { context: 'declarationList', positions: true });
  } catch {
    return null;
  }
  const declarations = [];
  csstree.walk(ast, {
    visit: 'Declaration',
    enter(node) {
      declarations.push({
        property: node.property,
        value: csstree.generate(node.value).trim(),
        source: source.slice(node.loc.start.offset, node.loc.end.offset).trim()
      });
    }
  });
  return declarations;
}

/**
 * Which pattern claims each property on an element. First pattern in registry
 * order wins, so a property can never be both stripped and reset.
 */
function decisionsFor(element, patterns) {
  const decisions = new Map();
  for (const { pattern, elements } of patterns) {
    if (!elements.has(element)) continue;
    for (const preservation of pattern.preserve || []) {
      const marker = pattern.markers?.[preservation.marker];
      if (!marker || !safeMatches(element, marker)) continue;
      if (!decisions.has(preservation.property)) {
        decisions.set(preservation.property, {
          action: 'preserve',
          to: preservation.as,
          kind: preservation.valueKind,
          pattern: pattern.id
        });
      }
    }
    for (const [property, literal] of Object.entries(pattern.reset || {})) {
      if (!decisions.has(property)) {
        decisions.set(property, { action: 'reset', literal, pattern: pattern.id });
      }
    }
    for (const [property, kind] of Object.entries(pattern.strip || {})) {
      if (!decisions.has(property)) {
        decisions.set(property, { action: 'strip', kind, pattern: pattern.id });
      }
    }
  }
  return decisions;
}

function applyDecisions(element, decisions, actions, record) {
  const styleText = element.getAttribute('style');
  if (!styleText) return false;
  const declarations = parseDeclarations(styleText);
  if (!declarations || declarations.length === 0) return false;

  const kept = [];
  let changed = false;
  const activePreservations = new Map();

  for (const declaration of declarations) {
    const decision = decisions.get(declaration.property);
    if (
      decision?.action === 'preserve' &&
      actions.has('preserve') &&
      decision.to &&
      valueMatchesKind(declaration.value, decision.kind)
    ) {
      activePreservations.set(decision.to, {
        decision,
        declaration
      });
    }
  }

  for (const declaration of declarations) {
    if (activePreservations.has(declaration.property)) {
      // A stale destination value must not beat the captured source value.
      // The source declaration is emitted at its original position below,
      // keeping the rewrite deterministic and idempotent.
      changed = true;
      continue;
    }

    const decision = decisions.get(declaration.property);
    if (!decision) {
      kept.push(declaration.source);
      continue;
    }

    if (decision.action === 'strip') {
      if (!actions.has('strip') || !valueMatchesKind(declaration.value, decision.kind)) {
        kept.push(declaration.source);
        continue;
      }
      changed = true;
      record({
        action: 'strip',
        pattern: decision.pattern,
        property: declaration.property,
        before: declaration.value,
        after: null
      });
      continue;
    }

    if (decision.action === 'preserve') {
      if (
        !actions.has('preserve') ||
        !decision.to ||
        !valueMatchesKind(declaration.value, decision.kind)
      ) {
        kept.push(declaration.source);
        continue;
      }
      changed = true;
      record({
        action: 'preserve',
        pattern: decision.pattern,
        property: declaration.property,
        before: declaration.value,
        after: `${decision.to}: ${declaration.value}`
      });
      kept.push(`${decision.to}: ${declaration.value}`);
      continue;
    }

    if (!actions.has('reset')) {
      kept.push(declaration.source);
      continue;
    }
    if (declaration.value === decision.literal) {
      kept.push(declaration.source);
      continue;
    }
    changed = true;
    record({
      action: 'reset',
      pattern: decision.pattern,
      property: declaration.property,
      before: declaration.value,
      after: decision.literal
    });
    kept.push(`${declaration.property}: ${decision.literal}`);
  }

  if (!changed) return false;
  if (kept.length === 0) element.removeAttribute('style');
  else element.setAttribute('style', `${kept.join('; ')};`);
  return true;
}

function describe(element) {
  const tag = element.tagName ? element.tagName.toLowerCase() : '?';
  const id = element.getAttribute && element.getAttribute('id');
  const className = (element.getAttribute && element.getAttribute('class')) || '';
  const classes = className.split(/\s+/).filter(Boolean).slice(0, 3).join('.');
  return `${tag}${id ? `#${id}` : ''}${classes ? `.${classes}` : ''}`;
}

function patternApplies(pattern, site, sectionId) {
  const scope = pattern.scope;
  if (!scope) return true;
  if (scope.site && scope.site !== site) return false;
  if (scope.sectionId && scope.sectionId !== sectionId) return false;
  return true;
}

/**
 * @param {Element} root                 section root; mutated in place
 * @param {object}  [options]
 * @param {string}  [options.site]       template site name, for scoped patterns
 * @param {string}  [options.sectionId]  template section id, for scoped patterns
 * @param {string[]}[options.actions]    subset of ALL_ACTIONS to perform
 * @returns {{changed: boolean, edits: object[]}}
 */
export function normalizeMotionResidue(root, options = {}) {
  const edits = [];
  if (!root || typeof root.querySelectorAll !== 'function') return { changed: false, edits };

  const site = options.site || null;
  const sectionId = options.sectionId || null;
  const actions = new Set(options.actions || ALL_ACTIONS);

  const active = [];
  for (const pattern of MOTION_PATTERNS) {
    if (!patternApplies(pattern, site, sectionId)) continue;
    const elements = elementsFor(root, pattern.markers || []);
    if (elements.size === 0) continue;
    active.push({ pattern, elements });
  }
  if (active.length === 0) return { changed: false, edits };

  let changed = false;
  const targets = new Set();
  for (const { elements } of active) for (const el of elements) targets.add(el);

  for (const element of targets) {
    const decisions = decisionsFor(element, active);
    if (decisions.size === 0) continue;
    const signature = describe(element);
    const touched = applyDecisions(element, decisions, actions, (edit) =>
      edits.push({ site, sectionId, signature, ...edit })
    );
    if (touched) changed = true;
  }

  for (const { pattern } of active) {
    if (actions.has('removeNodes')) {
      for (const element of elementsFor(root, pattern.removeNodes || [])) {
        if (element === root) continue;
        edits.push({
          site,
          sectionId,
          signature: describe(element),
          action: 'removeNodes',
          pattern: pattern.id,
          property: null,
          before: null,
          after: null
        });
        element.remove();
        changed = true;
      }
    }

    if (actions.has('neutralize')) {
      for (const element of elementsFor(root, pattern.neutralize || [])) {
        if (element.hasAttribute('data-pb-motion-inert')) continue;
        element.setAttribute('data-pb-motion-inert', '');
        element.setAttribute('aria-hidden', 'true');
        element.setAttribute('tabindex', '-1');
        edits.push({
          site,
          sectionId,
          signature: describe(element),
          action: 'neutralize',
          pattern: pattern.id,
          property: null,
          before: null,
          after: null
        });
        changed = true;
      }
    }

    const stamp = pattern.stamp;
    if (actions.has('stamp') && stamp && stamp.on) {
      for (const element of elementsFor(root, [stamp.on])) {
        if (element.getAttribute(stamp.name) === stamp.value) continue;
        element.setAttribute(stamp.name, stamp.value);
        edits.push({
          site,
          sectionId,
          signature: describe(element),
          action: 'stamp',
          pattern: pattern.id,
          property: stamp.name,
          before: null,
          after: stamp.value
        });
        changed = true;
      }
    }
  }

  return { changed, edits };
}

function parseMotionRoot(html, parseHTML) {
  const wrapped = `<div data-pb-motion-root>${html}</div>`;
  if (typeof parseHTML === 'function') {
    const parsed = parseHTML(wrapped);
    if (parsed?.querySelector) return parsed.querySelector('[data-pb-motion-root]');
    return null;
  }
  if (typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(wrapped, 'text/html');
  return doc.body?.firstElementChild || null;
}

/**
 * String-level wrapper for write paths that do not already hold a DOM root.
 * Uses normalizeMotionResidue() for the actual policy so parser, store,
 * composer and collab backstops cannot drift.
 *
 * @param {string} html
 * @param {object} [options]
 * @param {string} [options.site]
 * @param {string} [options.sectionId]
 * @param {function(string): object} [options.parseHTML]
 * @returns {string}
 */
export function normalizeMotionHTML(html, options = {}) {
  if (typeof html !== 'string' || !html) return html;
  let root;
  try {
    root = parseMotionRoot(html, options.parseHTML);
  } catch {
    return html;
  }
  if (!root) return html;
  const { changed } = normalizeMotionResidue(root, {
    site: options.site || null,
    sectionId: options.sectionId || null
  });
  return changed ? root.innerHTML : html;
}

/**
 * The pre-reveal rules the parser bakes into captured markup so reveal-library
 * content is visible without JS. Derived from the registry so the parser's
 * fallback and the runtime's observer cannot drift apart.
 *
 * `init` is the subset of `add` that only marks "runtime hooked" (aos-init):
 * the parser bakes it like the rest, but the runtime never strips it when
 * re-arming a below-fold element. The parser ignores the field.
 *
 * Order is not significant: no element in the corpus matches two reveal groups.
 */
export function scrollRevealRules() {
  const rules = [];
  for (const pattern of MOTION_PATTERNS) {
    for (const group of pattern.reveal || []) {
      rules.push({
        pattern: pattern.id,
        // Projected so consumers branch on the registry rather than on a
        // hardcoded id: the parser skips builder rows when baking classes, and
        // the runtime's mode operations act only on builder rows.
        owner: pattern.owner || 'template',
        selector: group.selector,
        add: group.add || [],
        remove: group.remove || [],
        init: group.init || []
      });
    }
  }
  return rules;
}

/**
 * Class names whose presence is controlled by the shared reveal runtime.
 * A crawl only captures one instant of those states, so CSS attribution must
 * never infer their lifetime ownership from snapshot matches alone.
 */
export function runtimeMotionStateClasses() {
  const classes = new Set();
  for (const rule of scrollRevealRules()) {
    for (const name of [...rule.add, ...rule.remove, ...rule.init]) {
      if (typeof name === 'string' && name) classes.add(name);
    }
  }
  return classes;
}

/**
 * Authored class names that identify elements owned by the shared reveal
 * runtime. Their base/start-state rules must stay in the same CSS bucket as
 * the runtime-mutated end-state rules; splitting the pair can reverse their
 * cascade once section scoping is added.
 */
export function runtimeMotionSelectorClasses() {
  const classes = new Set();
  for (const rule of scrollRevealRules()) {
    let ast;
    try {
      ast = csstree.parse(rule.selector, { context: 'selectorList' });
    } catch {
      continue;
    }
    csstree.walk(ast, {
      visit: 'ClassSelector',
      enter(node) {
        if (node.name) classes.add(node.name);
      }
    });
  }
  return classes;
}

/**
 * Actions a pattern declares that a given run is not performing. The parser
 * reports these so a deferred capability can never look like a handled one.
 */
export function deferredActionsFor(actions) {
  const enabled = new Set(actions || ALL_ACTIONS);
  const deferred = [];
  for (const pattern of MOTION_PATTERNS) {
    const declared = [];
    if ((pattern.preserve || []).length) declared.push('preserve');
    if (Object.keys(pattern.strip || {}).length) declared.push('strip');
    if (Object.keys(pattern.reset || {}).length) declared.push('reset');
    if ((pattern.removeNodes || []).length) declared.push('removeNodes');
    if ((pattern.neutralize || []).length) declared.push('neutralize');
    if (pattern.stamp) declared.push('stamp');
    const skipped = declared.filter((action) => !enabled.has(action));
    if (skipped.length) deferred.push({ pattern: pattern.id, skipped });
  }
  return deferred;
}
